/**
 * MEDIS v2 implementation of `GetAppointments`.
 * Routing (which calls use v2 and where they go) lives in `src/lib/medis/v2/client.ts`.
 *
 * v2 `POST /v2/GetAppointments`:
 *   - auth: Azure EasyAuth `X-ZUMO-AUTH` token (handled by `medisV2Api`)
 *   - body: `{ patient }`; the response is the inner `getAppointmentstResponse`
 *     (PascalCase), with the list under `Appointments` (`schedule` items). The
 *     `schedule` keys are documented lowercase — read case-insensitively via the
 *     helpers in `@/lib/medis/v2`.
 */
import {
  medisV2Api,
  MEDIS_V2_ROUTES,
  v2Url,
  unwrapV2,
  prop,
  asArray,
  parseV2Date,
  debugV2Shape,
  type GetAppointmentsResponseV2,
  type ScheduleV2,
} from '@/lib/medis/v2';
import type { Appointment } from '../types/appointment.types';

function mapAppointmentV2(dto: ScheduleV2): Appointment | null {
  const startTime = parseV2Date(prop<string>(dto, 'StartTime'));
  const endTime = parseV2Date(prop<string>(dto, 'EndTime'));
  if (!startTime || !endTime) return null;
  return {
    startTime,
    endTime,
    physicianId: prop<string>(dto, 'Physician') ?? '',
    specialityId: prop<string>(dto, 'Speciality') ?? '',
    locationId: prop<string>(dto, 'Location') ?? '',
    degree: prop<string>(dto, 'Degree') ?? '',
  };
}

/** All appointments (past + future) for a patient, via the v2 API. */
export async function getAppointmentsV2(patientFhirId: string): Promise<Appointment[]> {
  const route = MEDIS_V2_ROUTES.getAppointments;
  if (!route) throw new Error('v2 getAppointments route is disabled');

  const { data } = await medisV2Api.post(await v2Url(route), { patient: patientFhirId });
  const body = unwrapV2<GetAppointmentsResponseV2>(data);
  debugV2Shape('GetAppointments', body);

  return asArray<ScheduleV2>(prop(body, 'Appointments'))
    .map(mapAppointmentV2)
    .filter((a): a is Appointment => a !== null);
}
